import API from "./api";

// ✅ Get all products
export const getProducts = async () => {
    try {
        const response = await API.get("/products");
        return response.data;
    } catch (error) {
        console.error("Error fetching products:", error.response?.data || error.message);
        throw error;
    }
};

// ✅ Add a new product
export const addProduct = async (productData) => {
    try {
        const response = await API.post("/products/register", productData, {
            headers: { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error) {
        console.error("Error adding product:", error.response?.data || error.message);
        throw error;
    }
};

// Use the regular updateProduct function
export const updateProduct = async (productId, updatedData) => {
    try {
        // No need to do any special formatting for status since we're sending a complete object
        const response = await API.put(`/products/update/${productId}`,
            updatedData,
            { headers: { "Content-Type": "application/json" } }
        );

        return response.data;
    } catch (error) {
        console.error(`Error updating product (${productId}):`, error);
        throw new Error(error.response?.data?.message || "Product update failed.");
    }
};

// Fixed updateProductStatus function with correct endpoint
export const updateProductStatus = async (productId, status) => {
    try {
        // Step 1: Get current product data first (using the correct endpoint)
        const getProductResponse = await API.get(`/products`); // Get all products
        const currentProduct = getProductResponse.data.find(p => p.id === parseInt(productId));

        if (!currentProduct) {
            throw new Error("Product not found");
        }

        // Step 2: Format the status value
        const formattedStatus = status === "Y" || status === "1" || status === 1 ? "1" : "0";

        // Step 3: Create a complete product object with all fields and the new status
        const updatedProduct = {
            name: currentProduct.name,
            categoryId: currentProduct.category_id,
            brand: currentProduct.brand,
            country: currentProduct.country,
            price: parseFloat(currentProduct.price),
            description: currentProduct.description,
            isImported: currentProduct.is_imported === 1 || currentProduct.is_imported === true,
            status: formattedStatus
        };

        // Step 4: Send the complete updated product (using the correct endpoint)
        const response = await API.put(`/products/update/${productId}`,
            updatedProduct,
            { headers: { "Content-Type": "application/json" } }
        );

        return response.data;
    } catch (error) {
        // Improved error handling with more details
        console.error(`Error updating product status (${productId}):`, error);
        const errorMessage = error.response?.data?.message ||
            (error.response?.status === 404 ? "Endpoint not found" : "Status update failed");
        throw new Error(errorMessage);
    }
};

// ✅ Delete a product
export const deleteProduct = async (productId) => {
    try {
        const response = await API.delete(`/products/${productId}`);
        return response.data;
    } catch (error) {
        console.error("Error deleting product:", error);
        throw error;
    }
};

// ✅ Fetch unique filter values dynamically
export const getUniqueProductFilters = async () => {
    try {
        const response = await API.get("/products/filters"); // Ensure backend supports this endpoint
        return response.data;
    } catch (error) {
        console.error("Error fetching product filters:", error.response?.data || error.message);
        throw error;
    }
};

// ✅ Get product barcode by product ID
export const getProductBarcode = async (productId) => {
    try {
        const response = await API.get(`/products/getProductBarcode/${productId}`, {
            responseType: 'blob' // Important for handling image response
        });
        return URL.createObjectURL(response.data); // Correctly handle barcode image
    } catch (error) {
        if (error.response?.status === 404) {
            console.warn(`No barcode found for product ID: ${productId}`);
            return null;  // Return null if barcode is not found
        }
        console.error(`Error fetching barcode for product (${productId}):`, error);
        throw new Error("Barcode retrieval failed");
    }
};